import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ClockIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  PlayIcon,
  StopIcon,
  TrophyIcon,
  UserCircleIcon,
  BellIcon,
  XMarkIcon,
  CalendarIcon,
  BookmarkIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useUserStore } from '../store/userStore';
import { useSeatStore } from '../store/seatStore';
import { Seat } from '../types/index';
import { seatApi, sessionApi } from '../services/api';
import { ReservationCalendar } from './reservation/ReservationCalendar';
import { ReservationForm } from './reservation/ReservationForm';
import { MyReservations } from './reservation/MyReservations';
import SeatSelection from '../domains/seat/components/SeatSelection';
import { Button, Card, Alert } from './ui';
import DebugUserData from './DebugUserData';

const Dashboard: React.FC = () => {
  const { currentUser, currentSession, setCurrentSession } = useUserStore();
  const { seatStatus, setSeatStatus } = useSeatStore();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [studyTime, setStudyTime] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [showPromotion, setShowPromotion] = useState(true);

  // 예약 시스템 상태
  const [activeTab, setActiveTab] = useState<
    'realtime' | 'reservation' | 'myreservations'
  >('realtime');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSeat, setSelectedSeat] = useState<Seat | null>(null);
  const [showReservationForm, setShowReservationForm] = useState(false);

  // 현재 시간 업데이트
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);


  // 공부 시간 계산
  useEffect(() => {
    if (currentSession?.check_in_at) {
      const timer = setInterval(() => {
        const checkInTime = new Date(currentSession.check_in_at);
        const now = new Date();
        const diff = Math.floor((now.getTime() - checkInTime.getTime()) / 1000);

        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const seconds = diff % 60;

        setStudyTime({ hours, minutes, seconds });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [currentSession]);

  // 좌석 현황 로드
  useEffect(() => {
    const loadSeatStatus = async () => {
      try {
        const response = await seatApi.getAll();
        const seats = response.data || [];
        
        setSeatStatus({
          total_seats: seats.length,
          available_seats: seats.filter(
            (seat: any) => seat.status === 'available'
          ).length,
          occupied_seats: seats.filter(
            (seat: any) => seat.status === 'occupied'
          ).length,
          out_of_order_seats: seats.filter(
            (seat: any) => seat.status === 'disabled'
          ).length,
          reserved_seats: seats.filter(
            (seat: any) => seat.status === 'reserved'
          ).length,
          occupancy_rate: Math.round(
            (seats.filter((seat: any) => seat.status === 'occupied').length /
              seats.length) *
              100
          ) || 0,
          last_updated: new Date().toISOString(),
        });
      } catch (error) {
        console.error('Failed to load seat status:', error);
      }
    };

    loadSeatStatus();
    const interval = setInterval(loadSeatStatus, 30000);

    return () => clearInterval(interval);
  }, [setSeatStatus]);

  const handleCheckOut = async () => {
    if (!currentSession) return;

    try {
      const response = await sessionApi.checkOut(currentSession.id);
      setCurrentSession(null);
      alert(`퇴실이 완료되었습니다. 총 공부시간: ${response.total_minutes}분`);
    } catch (error) {
      console.error('Check-out failed:', error);
      alert('퇴실 처리 중 오류가 발생했습니다.');
    }
  };

  const formatTime = (time: Date) => {
    return time.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  interface QuickAction {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    color: string;
    href?: string;
    action?: () => void;
  }

  const quickActions: QuickAction[] = [
    {
      icon: BuildingOfficeIcon,
      label: '좌석선택',
      href: '/dashboard/seats',
      color: 'text-white',
    },
    {
      icon: CalendarIcon,
      label: '예약하기',
      action: () => setActiveTab('reservation'),
      color: 'text-white',
    },
    {
      icon: TrophyIcon,
      label: '순위',
      href: '/dashboard/rankings',
      color: 'text-white',
    },
    {
      icon: UserCircleIcon,
      label: '내정보',
      href: '/dashboard/profile',
      color: 'text-white',
    },
  ];

  // 예약 완료 핸들러
  const handleReservationComplete = () => {
    setShowReservationForm(false);
    setSelectedSeat(null);
    setActiveTab('myreservations');
  };

  // 좌석 선택 핸들러
  const handleSeatSelect = (seat: Seat) => {
    setSelectedSeat(seat);
    if (activeTab === 'reservation') {
      setShowReservationForm(true);
    }
  };

  return (
    <div className="min-h-screen bg-secondary-50 pb-20 lg:pb-8">
      <div className="px-4 lg:px-6 space-y-4 lg:space-y-6 pt-4">
        {/* 메인 카드 - 현재 세션 또는 환영 메시지 */}
        <div className="lg:grid lg:grid-cols-3 lg:gap-6">
          <div className="lg:col-span-2">
            {currentSession ? (
              <div className="card-gradient-success rounded-2xl p-6 text-white slide-up">
                {/* 상단: 좌석 정보와 시간 */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-2">
                    <ClockIcon className="h-5 w-5 text-white opacity-90" />
                    <span className="text-sm opacity-90">
                      {currentSession.seat_id}번 좌석
                    </span>
                  </div>
                  <span className="text-sm opacity-90">
                    {formatTime(currentTime)}
                  </span>
                </div>

                {/* 빠른 액션 아이콘들 */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  {quickActions.map((action, index) =>
                    action.href ? (
                      <Link
                        key={index}
                        to={action.href}
                        className="flex flex-col items-center space-y-2 hover-scale transition-colors"
                      >
                        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                          <action.icon className={`h-6 w-6 ${action.color}`} />
                        </div>
                        <span className="text-xs text-center">
                          {action.label}
                        </span>
                      </Link>
                    ) : (
                      <button
                        key={index}
                        onClick={action.action}
                        className="flex flex-col items-center space-y-2 hover-scale transition-colors"
                      >
                        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                          <action.icon className={`h-6 w-6 ${action.color}`} />
                        </div>
                        <span className="text-xs text-center">
                          {action.label}
                        </span>
                      </button>
                    )
                  )}
                </div>

                {/* 공부시간 표시 */}
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold mb-1">
                    {String(studyTime.hours).padStart(2, '0')}:
                    {String(studyTime.minutes).padStart(2, '0')}:
                    {String(studyTime.seconds).padStart(2, '0')}
                  </div>
                  <div className="text-sm opacity-90">순공부시간</div>
                </div>

                {/* 액션 버튼들 */}
                <div className="flex space-x-3">
                  <button
                    onClick={handleCheckOut}
                    className="flex-1 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl py-3 text-center font-medium hover:bg-opacity-30 transition-colors"
                  >
                    퇴실하기
                  </button>
                  <button className="flex-1 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl py-3 text-center font-medium hover:bg-opacity-30 transition-colors">
                    시간연장
                  </button>
                </div>

                {/* 입실 시간 */}
                <div className="text-center mt-4">
                  <span className="text-xs opacity-75">
                    입실:{' '}
                    {new Date(currentSession.check_in_at).toLocaleString(
                      'ko-KR'
                    )}
                  </span>
                </div>
              </div>
            ) : (
              <div className="card-gradient-primary rounded-2xl p-6 text-white slide-up">
                {/* 공지사항 헤더 */}
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold mb-2 text-black">
                    📢 공지사항
                  </h2>
                  <p className="text-sm text-black">독서실 이용 안내</p>
                </div>

                {/* 공지사항 내용 */}
                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <h3 className="text-sm font-medium mb-2 !text-black">
                      📅 2024년 12월 운영 안내
                    </h3>
                    <p className="text-xs leading-relaxed !text-black">
                      • 평일: 06:00 - 24:00
                      <br />
                      • 주말: 07:00 - 23:00
                      <br />• 연말연시(12/31-1/1) 휴관
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <h3 className="text-sm font-medium mb-2 !text-black">
                      🔔 이용 수칙
                    </h3>
                    <p className="text-xs leading-relaxed !text-black">
                      • 조용한 면학 분위기 유지 협조
                      <br />
                      • 개인 물품 분실 주의
                      <br />• 음식물 반입 금지
                    </p>
                  </div>
                </div>

                {/* 좌석 선택/예약 탭 */}
                <div className="mt-6">
                  {/* 탭 메뉴 */}
                  <div className="flex bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-1 mb-4">
                    <button
                      onClick={() => setActiveTab('realtime')}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                        activeTab === 'realtime'
                          ? 'bg-white bg-opacity-30 text-white'
                          : 'text-white text-opacity-70'
                      }`}
                    >
                      <PlayIcon className="h-4 w-4 inline mr-1" />
                      실시간 선택
                    </button>
                    <button
                      onClick={() => setActiveTab('reservation')}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                        activeTab === 'reservation'
                          ? 'bg-white bg-opacity-30 text-white'
                          : 'text-white text-opacity-70'
                      }`}
                    >
                      <CalendarIcon className="h-4 w-4 inline mr-1" />
                      예약하기
                    </button>
                    <button
                      onClick={() => setActiveTab('myreservations')}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                        activeTab === 'myreservations'
                          ? 'bg-white bg-opacity-30 text-white'
                          : 'text-white text-opacity-70'
                      }`}
                    >
                      <BookmarkIcon className="h-4 w-4 inline mr-1" />내 예약
                    </button>
                  </div>

                  {/* 실시간 좌석 선택 */}
                  {activeTab === 'realtime' && (
                    <Link
                      to="/dashboard/seats"
                      style={{ color: 'white !important' }}
                      className="block bg-white bg-opacity-30 backdrop-blur-sm rounded-xl py-4 text-center font-medium hover:bg-opacity-40 transition-colors border border-white border-opacity-20"
                    >
                      <PlayIcon
                        style={{ color: 'white !important' }}
                        className="h-5 w-5 inline mr-2"
                      />
                      좌석 선택하기
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 데스크톱 사이드 카드 */}
          <div className="hidden lg:block space-y-4">
            {/* 빠른 액션 */}
            <div className="card rounded-xl p-4">
              <h3 className="text-sm font-medium text-secondary-900 mb-3">
                빠른 액션
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <Link
                  to="/dashboard/seats"
                  className="btn btn-primary btn-sm hover-lift transition-colors"
                >
                  <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                  좌석선택
                </Link>
                <Link
                  to="/dashboard/rankings"
                  className="btn btn-secondary btn-sm hover-lift transition-colors"
                >
                  <TrophyIcon className="h-4 w-4 mr-1" />
                  순위
                </Link>
                <Link
                  to="/dashboard/profile"
                  className="btn btn-secondary btn-sm hover-lift transition-colors"
                >
                  <UserCircleIcon className="h-4 w-4 mr-1" />
                  내정보
                </Link>
                <Link
                  to="/dashboard/stats"
                  className="btn btn-secondary btn-sm hover-lift transition-colors"
                >
                  <ChartBarIcon className="h-4 w-4 mr-1" />
                  통계
                </Link>
              </div>
            </div>

            {/* 좌석 현황 */}
            {seatStatus && (
              <div className="card rounded-xl p-4">
                <h3 className="text-sm font-medium text-secondary-900 mb-3">
                  좌석 현황
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-secondary-600">
                      이용 가능
                    </span>
                    <span className="font-bold text-success-600">
                      {seatStatus.available_seats}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-secondary-600">이용중</span>
                    <span className="font-bold text-warning-600">
                      {seatStatus.occupied_seats}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-secondary-600">점검중</span>
                    <span className="font-bold text-error-600">
                      {seatStatus.out_of_order_seats}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 추가 정보 카드들 - 모바일만 */}
        <div className="lg:hidden space-y-4">
          {/* 잔여 시간 카드 */}
          <div className="card rounded-xl p-4 flex items-center justify-between bounce-in">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-success-100 rounded-full flex items-center justify-center">
                <ClockIcon className="h-5 w-5 text-success-600" />
              </div>
              <div>
                <div className="font-medium text-secondary-900">잔여 시간</div>
                <div className="text-sm text-secondary-500">
                  오늘 이용 가능한 시간
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-secondary-900">
                8시간 30분
              </div>
              <div className="text-xs text-secondary-500">+480분</div>
            </div>
          </div>

          {/* 이번 주 공부 통계 */}
          <div
            className="card rounded-xl p-4 bounce-in"
            style={{ animationDelay: '0.1s' }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-secondary-900">
                이번 주 공부 시간
              </h3>
              <ChartBarIcon className="h-5 w-5 text-secondary-400" />
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <div className="text-2xl font-bold text-secondary-900 mb-1">
                  25시간
                </div>
                <div className="text-xs text-secondary-500">목표: 30시간</div>
              </div>
              <div className="w-20 h-2 bg-secondary-200 rounded-full">
                <div className="w-4/5 h-2 bg-success-500 rounded-full"></div>
              </div>
            </div>
          </div>

          {/* 순위 정보 */}
          <div
            className="card rounded-xl p-4 bounce-in"
            style={{ animationDelay: '0.2s' }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-secondary-900">이번 달 순위</h3>
              <TrophyIcon className="h-5 w-5 text-warning-500" />
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <div className="text-2xl font-bold text-secondary-900 mb-1">
                  #12
                </div>
                <div className="text-xs text-secondary-500">전체 156명 중</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-warning-600">
                  상위 8%
                </div>
                <div className="text-xs text-secondary-500">지난 달보다 ↑3</div>
              </div>
            </div>
          </div>
        </div>

        {/* 데스크톱 추가 카드들 */}
        <div className="hidden lg:block">
          <div className="grid grid-cols-3 gap-6 mb-6">
            {/* 주간 통계 */}
            <div className="card rounded-xl p-6 text-center">
              <ChartBarIcon className="h-8 w-8 text-primary-600 mx-auto mb-2" />
              <h3 className="text-lg font-bold text-secondary-900 mb-1">
                25시간
              </h3>
              <p className="text-sm text-secondary-500">이번 주 공부시간</p>
            </div>

            {/* 순위 */}
            <div className="card rounded-xl p-6 text-center">
              <TrophyIcon className="h-8 w-8 text-warning-500 mx-auto mb-2" />
              <h3 className="text-lg font-bold text-secondary-900 mb-1">#12</h3>
              <p className="text-sm text-secondary-500">이번 달 순위</p>
            </div>

            {/* 잔여 시간 */}
            <div className="card rounded-xl p-6 text-center">
              <ClockIcon className="h-8 w-8 text-success-600 mx-auto mb-2" />
              <h3 className="text-lg font-bold text-secondary-900 mb-1">
                8시간 30분
              </h3>
              <p className="text-sm text-secondary-500">잔여 시간</p>
            </div>
          </div>
        </div>

        {/* 예약 시스템 */}
        {(activeTab === 'reservation' || activeTab === 'myreservations') && (
          <div className="space-y-6">
            {activeTab === 'reservation' && (
              <>
                {/* 예약 폼 모달 */}
                {showReservationForm && selectedSeat && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="w-full max-w-md">
                      <ReservationForm
                        selectedDate={selectedDate}
                        selectedSeat={selectedSeat}
                        onReservationComplete={handleReservationComplete}
                        onCancel={() => {
                          setShowReservationForm(false);
                          setSelectedSeat(null);
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* 예약 달력과 좌석 선택 */}
                <div className="grid lg:grid-cols-2 gap-6">
                  <ReservationCalendar
                    selectedDate={selectedDate}
                    onDateSelect={setSelectedDate}
                  />

                  <div className="space-y-4">
                    <div className="bg-white rounded-lg shadow-md p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        {selectedDate.toLocaleDateString('ko-KR')} 좌석 선택
                      </h3>
                      <SeatSelection onSeatSelect={handleSeatSelect} />
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'myreservations' && <MyReservations />}
          </div>
        )}

        {/* 안전한 금융기관 정보 (K뱅크 스타일 모방) - 모바일만 */}
        {activeTab === 'realtime' && (
          <div
            className="lg:hidden card rounded-xl p-4 bg-gradient-to-r from-secondary-50 to-primary-50 border-0 bounce-in"
            style={{ animationDelay: '0.3s' }}
          >
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                <BuildingOfficeIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-secondary-900 mb-1">
                  안전한 독서실 이용을 위해
                </h3>
                <p className="text-xs text-secondary-600">
                  개인 물품 관리에 유의해 주시고, <br />
                  조용한 면학 분위기 유지에 협조해 주세요.
                </p>
              </div>
              <div className="flex-shrink-0">
                <div className="w-16 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <span className="text-xs font-medium text-primary-700">
                    📚
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <DebugUserData />
    </div>
  );
};

export default Dashboard;
